import React, { useState, useMemo } from 'react';
import './PoolPerformanceChart.css';

const PoolPerformanceChart = ({ data, timeframe }) => {
  const [selectedPools, setSelectedPools] = useState(new Set());
  const [chartType, setChartType] = useState('cumulative'); // 'cumulative' or 'daily'
  const [showAll, setShowAll] = useState(true);

  // Process and prepare chart data
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return { dates: [], pools: [] };

    // Get all unique dates across all pools
    const allDates = new Set();
    data.forEach(pool => {
      pool.performanceHistory.forEach(point => {
        allDates.add(point.date);
      });
    });

    const sortedDates = Array.from(allDates).sort();

    // Prepare pool data for charting
    const poolsData = data.map(pool => {
      const performanceMap = new Map();
      pool.performanceHistory.forEach(point => {
        performanceMap.set(point.date, point);
      });

      // Fill in missing dates with interpolated or last known values
      const filledData = sortedDates.map(date => {
        const point = performanceMap.get(date);
        if (point) {
          return {
            date,
            value: chartType === 'cumulative' ? point.cumulativeReturn : point.dailyReturn,
            sharePrice: point.sharePrice,
            tradesExecuted: point.tradesExecuted
          };
        }
        // Use last known value if no data for this date
        const lastKnownIndex = sortedDates.findIndex(d => d === date) - 1;
        if (lastKnownIndex >= 0) {
          const lastKnownDate = sortedDates[lastKnownIndex];
          const lastKnownPoint = performanceMap.get(lastKnownDate);
          if (lastKnownPoint) {
            return {
              date,
              value: chartType === 'cumulative' ? lastKnownPoint.cumulativeReturn : 0,
              sharePrice: lastKnownPoint.sharePrice,
              tradesExecuted: 0
            };
          }
        }
        return {
          date,
          value: 0,
          sharePrice: 1,
          tradesExecuted: 0
        };
      });

      return {
        ...pool,
        chartData: filledData,
        color: getPoolColor(pool.id),
        latestReturn: filledData[filledData.length - 1]?.value || 0
      };
    });

    return {
      dates: sortedDates,
      pools: poolsData
    };
  }, [data, chartType]);

  // Generate consistent colors for pools
  const getPoolColor = (poolId) => {
    const colors = [
      '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
      '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
    ];
    return colors[poolId % colors.length];
  };

  // Calculate chart dimensions and scales
  const chartWidth = 800;
  const chartHeight = 400;
  const margin = { top: 20, right: 120, bottom: 60, left: 80 };
  const innerWidth = chartWidth - margin.left - margin.right;
  const innerHeight = chartHeight - margin.top - margin.bottom;

  // Get min/max values for scaling
  const allValues = chartData.pools.flatMap(pool => 
    pool.chartData.map(point => point.value)
  );
  const minValue = Math.min(...allValues, 0);
  const maxValue = Math.max(...allValues, 0);
  const valueRange = maxValue - minValue;
  const padding = valueRange * 0.1;

  // Scale functions
  const xScale = (dateIndex) => (dateIndex / (chartData.dates.length - 1)) * innerWidth;
  const yScale = (value) => innerHeight - ((value - minValue + padding) / (valueRange + 2 * padding)) * innerHeight;

  // Toggle pool selection
  const togglePool = (poolId) => {
    const newSelected = new Set(selectedPools);
    if (newSelected.has(poolId)) {
      newSelected.delete(poolId);
    } else {
      newSelected.add(poolId);
    }
    setSelectedPools(newSelected);
    setShowAll(false);
  };

  // Get pools to display
  const poolsToShow = showAll ? chartData.pools : 
    chartData.pools.filter(pool => selectedPools.has(pool.id));

  // Generate SVG path for a pool's performance line
  const generatePath = (poolData) => {
    if (!poolData.chartData || poolData.chartData.length === 0) return '';
    
    return poolData.chartData
      .map((point, index) => {
        const x = xScale(index);
        const y = yScale(point.value);
        return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
      })
      .join(' ');
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Format percentage
  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  if (!data || data.length === 0) {
    return (
      <div className="pool-performance-chart empty">
        <div className="empty-state">
          <h4>No Performance Data Available</h4>
          <p>Pool performance data will appear here once pools have trading history.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pool-performance-chart">
      <div className="chart-controls">
        <div className="chart-type-selector">
          <button
            className={`chart-type-btn ${chartType === 'cumulative' ? 'active' : ''}`}
            onClick={() => setChartType('cumulative')}
          >
            Cumulative Returns
          </button>
          <button
            className={`chart-type-btn ${chartType === 'daily' ? 'active' : ''}`}
            onClick={() => setChartType('daily')}
          >
            Daily Returns
          </button>
        </div>

        <div className="view-controls">
          <button
            className={`view-btn ${showAll ? 'active' : ''}`}
            onClick={() => setShowAll(true)}
          >
            Show All
          </button>
          <button
            className={`view-btn ${!showAll ? 'active' : ''}`}
            onClick={() => setShowAll(false)}
          >
            Selected Only
          </button>
        </div>
      </div>

      <div className="chart-container">
        <svg width={chartWidth} height={chartHeight} className="performance-chart-svg">
          {/* Grid lines */}
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#f0f0f0" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width={innerWidth} height={innerHeight} x={margin.left} y={margin.top} fill="url(#grid)" />

          {/* Zero line */}
          <line
            x1={margin.left}
            y1={yScale(0)}
            x2={margin.left + innerWidth}
            y2={yScale(0)}
            stroke="#666"
            strokeWidth="1"
            strokeDasharray="3,3"
          />

          {/* Performance lines */}
          {poolsToShow.map(pool => (
            <g key={pool.id}>
              <path
                d={generatePath(pool)}
                fill="none"
                stroke={pool.color}
                strokeWidth="2"
                transform={`translate(${margin.left}, ${margin.top})`}
                className="performance-line"
              />
              
              {/* Data points */}
              {pool.chartData.map((point, index) => (
                <circle
                  key={index}
                  cx={margin.left + xScale(index)}
                  cy={margin.top + yScale(point.value)}
                  r="3"
                  fill={pool.color}
                  className="data-point"
                >
                  <title>
                    {pool.name}: {formatPercentage(point.value)} on {formatDate(point.date)}
                  </title>
                </circle>
              ))}
            </g>
          ))}

          {/* X-axis */}
          <g transform={`translate(${margin.left}, ${margin.top + innerHeight})`}>
            <line x1="0" y1="0" x2={innerWidth} y2="0" stroke="#333" />
            {chartData.dates.map((date, index) => {
              if (index % Math.ceil(chartData.dates.length / 6) === 0) {
                return (
                  <g key={index} transform={`translate(${xScale(index)}, 0)`}>
                    <line y1="0" y2="5" stroke="#333" />
                    <text y="20" textAnchor="middle" fontSize="12" fill="#666">
                      {formatDate(date)}
                    </text>
                  </g>
                );
              }
              return null;
            })}
          </g>

          {/* Y-axis */}
          <g transform={`translate(${margin.left}, ${margin.top})`}>
            <line x1="0" y1="0" x2="0" y2={innerHeight} stroke="#333" />
            {Array.from({ length: 6 }, (_, i) => {
              const value = minValue - padding + (i / 5) * (valueRange + 2 * padding);
              return (
                <g key={i} transform={`translate(0, ${yScale(value)})`}>
                  <line x1="-5" y1="0" x2="0" y2="0" stroke="#333" />
                  <text x="-10" y="4" textAnchor="end" fontSize="12" fill="#666">
                    {formatPercentage(value)}
                  </text>
                </g>
              );
            })}
          </g>

          {/* Chart title */}
          <text x={chartWidth / 2} y="15" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333">
            Pool Performance Comparison ({timeframe})
          </text>
        </svg>

        {/* Legend */}
        <div className="chart-legend">
          <h4>Investment Pools</h4>
          <div className="legend-items">
            {chartData.pools.map(pool => (
              <div
                key={pool.id}
                className={`legend-item ${selectedPools.has(pool.id) || showAll ? 'active' : 'inactive'}`}
                onClick={() => togglePool(pool.id)}
              >
                <div
                  className="legend-color"
                  style={{ backgroundColor: pool.color }}
                ></div>
                <div className="legend-info">
                  <div className="legend-name">{pool.name}</div>
                  <div className="legend-details">
                    <span className="legend-strategy">{pool.strategy}</span>
                    <span className={`legend-return ${pool.latestReturn >= 0 ? 'positive' : 'negative'}`}>
                      {formatPercentage(pool.latestReturn)}
                    </span>
                  </div>
                  <div className="legend-stats">
                    <span className="legend-aum">{pool.totalSize.toFixed(2)} SOL</span>
                    <span className="legend-risk">{pool.riskLevel} risk</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="chart-summary">
        <div className="summary-stats">
          <div className="stat">
            <span className="stat-label">Best Performer</span>
            <span className="stat-value positive">
              {(() => {
                const best = chartData.pools.reduce((best, pool) => 
                  pool.latestReturn > (best?.latestReturn || -Infinity) ? pool : best
                , null);
                return best ? `${best.name}: ${formatPercentage(best.latestReturn)}` : 'N/A';
              })()}
            </span>
          </div>
          
          <div className="stat">
            <span className="stat-label">Average Return</span>
            <span className="stat-value">
              {(() => {
                const avgReturn = chartData.pools.reduce((sum, pool) => sum + pool.latestReturn, 0) / chartData.pools.length;
                return formatPercentage(avgReturn);
              })()}
            </span>
          </div>
          
          <div className="stat">
            <span className="stat-label">Total Pools</span>
            <span className="stat-value">{chartData.pools.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PoolPerformanceChart;